import React, { useEffect, useState } from "react";
import Form from "@rjsf/core";
import validator from "@rjsf/validator-ajv8";
import { useHistory, useParams } from "react-router-dom";
import { groupBy, uniq } from "underscore";
import { useSocket } from "../providers/socketProvider";
import { Backup } from "./backup";
import "./collectionView.css";
import { SectionView } from "./sectionView";
import { useDefinitions } from "../providers/DefinitionProvider";

interface CollectionViewState {
  filter: string;
  collection: any[];
  collectionFiltered: any[];
  currentItem: any;
}

const isUrl = (value: string) => {
  try {
    new URL(value);
    return true;
  } catch (_) {
    return false;
  }
};

const CustomWidgetWithLink = (props) => {
  let href = props.value;
  let validUrl = true;
  if (!isUrl(props.value)) {
    let collectionUrl = "";
    if (props.name === "settingId") {
      collectionUrl = props.options.context === "boAdapter" ? "settingboad/" : "setting/";
    } else if (props.name === "dictionaryId") {
      collectionUrl = "dictionary/";
    } else if ((props.name as string).indexOf("videoId") !== -1) {
      collectionUrl = props.options.context === "boAdapter" ? "videodataboad/" : "videometadata/";
    } else {
      validUrl = false;
    }

    href = `/${collectionUrl}${props.value}`;
  }
  return (
    <>
      {validUrl && props.value !== "" && (
        <a
          href={href}
          target="_self"
          style={{ marginLeft: "6px" }}
          aria-label={`Link to ${props.value}`}
          title={`Link to ${props.value}`}
        >
          <i className={"glyphicon glyphicon-link"} />
        </a>
      )}
      <input
        id={props.id}
        className="form-control"
        value={props.value}
        required={props.required}
        onChange={(event) => props.onChange(event.target.value)}
      />
    </>
  );
};

const schemaForCollectionName = (defs, collectionName: string) => {
  return {
    definitions: defs[collectionName].definitions,
    $ref: defs.__specs__[collectionName].$ref,
  };
};

export function CollectionView() {
  const socket = useSocket();
  const { current, collectionName } = useParams<{
    current: string;
    collectionName: string;
  }>();
  const history = useHistory();
  const defs = useDefinitions();

  const collectionDefs = defs.__specs__[collectionName];
  const collectionKey = collectionDefs.primaryKey;
  const collectionDisplayName = collectionDefs.displayName;
  const displayColumns = collectionDefs.displayColumns;
  const context = collectionDefs.context;

  const [state, setState] = useState<CollectionViewState>({
    filter: "",
    collection: [],
    collectionFiltered: [],
    currentItem: null,
  });

  useEffect(() => {
    setState((state) => ({
      ...state,
      currentItem: state.collection.find((it) => it[collectionKey] === current),
    }));
  }, [current, collectionName, state.collection, collectionKey]);

  useEffect(() => {
    socket.on(`${collectionName}-full`, (data) =>
      setState((state) => ({ ...state, collection: data.items }))
    );
    socket.emit(`${collectionName}-full-request`);
    return () => {
      setState((state) => ({ ...state, collection: [] }));
      socket.off(`${collectionName}-full`);
    };
  }, [collectionName, socket]);

  useEffect(() => {
    socket.on(`${collectionName}-update-notification`, (doc: any) => {
      doc[collectionKey] === current &&
        alert(
          "This document has been modified by someone else!\nPlease, reload"
        );
    });
    return () => {
      socket.off(`${collectionName}-update-notification`);
    };
  }, [collectionKey, collectionName, current, socket]);

  useEffect(() => {
    const r = new RegExp(state.filter, "i");
    const collectionFiltered = state.collection
      .sort((a, b) => {
        const key = (k: any): string => (k[collectionKey] || "").toUpperCase();
        return key(a) > key(b) ? 1 : -1;
      })
      .filter((it) => (it[collectionKey] || "").match(r));
    setState((state) => ({ ...state, collectionFiltered }));
  }, [collectionKey, state.collection, state.filter]);

  const fullBySection = groupBy(
    uniq(state.collectionFiltered, false, collectionKey),
    "section"
  );

  const handleRestored = () => {
    socket.emit(`${collectionName}-full-request`);
  };

  // Check if the app is running in localhost or in production.
  // If it is in localhost, the client and the server app are running on different domain (ports), so we need to specify it.
  const jsonapiUrl =
    window.location.hostname === "localhost" ? "http://localhost:3000/" : "/";

  return (
    <div className="collection-view row">
      <div className="collection-view__sidenav col-sm-3" style={{}}>
        <h4>{collectionDisplayName}</h4>

        <form className="form-inline">
          <input
            className="form-control"
            style={{ width: "100%" }}
            type="search"
            placeholder="Filter"
            aria-label="Search"
            value={state.filter}
            onChange={(e) => setState({ ...state, filter: e.target.value })}
          />
        </form>
        <ul className="list-group">
          {Object.keys(fullBySection).map((section) => (
            <SectionView
              key={section}
              sectionName={section}
              items={fullBySection[section]}
              collectionName={collectionName}
              collectionKey={collectionKey}
              currentItemId={current}
              requestExpanded={
                state.currentItem?.section === section || (state.filter && true)
              }
            />
          ))}
        </ul>
        <Backup collections={[collectionName]} onUpload={handleRestored} />
      </div>

      <div className="collection-view__content col-sm-9">
        <div
          style={{ paddingBottom: "1em", borderBottom: "1px solid #e7e7e7" }}
        >
          <h2>{current}</h2>
          {current && (
            <a href={`${jsonapiUrl}api/${collectionName}/${current}`}>
              jsonapi
            </a>
          )}
        </div>

        <Form
          schema={schemaForCollectionName(defs, collectionName)}
          uiSchema={{
            action: { "ui:widget": "textarea" },
            expectedResult: { "ui:widget": "textarea" },
            platforms: { "ui:widget": "checkboxes" },
            automated: { "ui:widget": "checkboxes" },
            divaLaunchParams: {
              settingId: {
                "ui:widget": CustomWidgetWithLink,
                "ui:options": { context },
              },
              dictionaryId: {
                "ui:widget": CustomWidgetWithLink,
                "ui:options": { context },
              },
              videoId: {
                items: {
                  "ui:widget": CustomWidgetWithLink,
                  "ui:options": { context },
                },
              },
            },
          }}
          formData={state.currentItem}
          // onChange={(data) => console.log("form changed", data)}
          onSubmit={(data) => {
            socket.emit(`${collectionName}-update`, data.formData);
            socket.emit(`${collectionName}-full-request`);
          }}
          className={`rjsf ${displayColumns}`}
          onError={(e) => console.error(e)}
          validator={validator as unknown as any}
        >
          <div className="collection-view__buttons">
            <button
              type="button"
              className="btn btn-danger"
              onClick={() => {
                if (
                  window.confirm("Are you sure you wish to delete this item?")
                ) {
                  socket.emit(`${collectionName}-remove`, state.currentItem);
                  socket.emit(`${collectionName}-full-request`);
                  history.replace(`/${collectionName}`);
                  window.scrollTo(0, 0);
                }
              }}
            >
              Remove
            </button>{" "}
            <button type="submit" className="btn btn-success">
              Save
            </button>
          </div>
        </Form>
      </div>
    </div>
  );
}
